//shared containers
import { Footer } from '@/containers-m/Footer';
import { Navigation } from '/containers-m/';

//containers
import {
  Hero,
  WatchItRunYourFreight,
  HowItWorks,
  RunsOnYourStack,
  TheRestOfTheLineup,
  EndCTA,
} from '../containers';

//styles
import {
  PageWrapper,
  WarehouseCustomerServiceGlobalStyle,
} from './WarehouseCustomerService.styles';

const hero = {
  slug: 'warehouse-customer-service',
  title: 'Warehouse Customer Service',
  description:
    'Answers the emails and calls your warehouse gets all day about arrivals, receipts and ship dates, books or moves appointments, and updates ETAs, using your dock schedule and your WMS.',
  metric: '65–75%',
  metricLabel: 'of customer questions answered without a person',
};

const watchItRun = {
  title: 'A store wants to know where its order is',
  stageHeadTitle:
    'A store just emailed: did PO 4471 arrive, and when does our order ship?',
  stageHeadMeta: 'Customer Service · PO 4471',
  rows: [
    {
      time: '11:02:10',
      text: 'Email from a store manager about inbound PO 4471 and outbound order 88213.',
    },
    {
      time: '11:02:14',
      text: 'Dock schedule: PO 4471 unloaded at Door 6 at 10:40.',
    },
    {
      time: '11:02:18',
      text: 'WMS: 46 of 48 cartons received. Order 88213 picked, shipping tomorrow at 9am.',
    },
    {
      time: '11:02:25',
      text: 'Replied with the receipt, the ship date and the 2-carton shortage.',
    },
    {
      time: '11:02:30',
      text: 'Answered in 20 seconds. The shortage is with a rep, receipt and BOL attached.',
    },
  ],
  summaryText:
    'A store asked two questions that usually take two systems and a few minutes to answer. Loadsmart AI checked the dock schedule and the WMS, replied with the receipt and the ship date, and passed the carton shortage to a rep with everything needed to resolve it.',
  agentBadge: 'Customer Service Agent',
  escalationTitle: 'Problems go to a person, with the records attached.',
  escalationText:
    'The agent answers what your records can answer. A refused load, a quantity mismatch or an upset customer goes to one of your reps with the conversation and the records already pulled, so they can resolve it without searching the inbox first.',
};

const howItWorks = {
  stepsTitleWhite: 'Answers the routine questions,',
  stepsTitleGradient: 'and hands the problems to your team.',
  steps: [
    {
      title: 'Reads every request',
      description:
        'Works out what each email or call is asking, and which order, PO or appointment it is about.',
    },
    {
      title: 'Answers from your systems',
      description:
        'Combines dock appointment status with WMS receipt and inventory status, and replies with the facts.',
    },
    {
      title: 'Books appointments and updates ETAs',
      description:
        'Creates, reschedules or cancels an appointment when an email asks, and updates the ETA when a carrier sends a new one.',
    },
    {
      title: 'Escalates the exceptions',
      description:
        'Sends refused loads, shortages and complaints to a rep with the records attached.',
    },
  ],
  outcomes: [
    {
      title: 'Answers in seconds',
      description:
        'Routine status questions get a reply while the sender is still at their desk.',
    },
    {
      title: 'Reps work on the problems',
      description:
        'Your team spends its day on refused loads and shortages, the work that needs a person.',
    },
    {
      title: 'Every request is on record',
      description:
        'Each question, answer and appointment change is logged against the order or PO it concerns.',
    },
  ],
};

const lineupCards = [
  {
    slug: 'scheduling-and-re-scheduling',
    title: 'Scheduling & Re-Scheduling',
    description: 'Rebooks the appointment when a carrier runs late.',
  },
  {
    slug: 'tracking-and-status-updates',
    title: 'Tracking & Status Updates',
    description: 'Supplies the live ETA behind every where-is-my-load answer.',
  },
  {
    slug: 'document-collection-and-filing',
    title: 'Document Collection & Filing',
    description:
      'We capture every quote, tender, rate con, BOL, POD and invoice as it arrives, and file it against the right shipment automatically.',
  },
  {
    slug: 'tender-failures',
    title: 'Tender Failures',
    description:
      'When a tender falls through, we re-tender down your routing guide and re-book, before the miss ever reaches your team.',
  },
  {
    slug: 'proactive-load-audits',
    title: 'Proactive Load Audits',
    description:
      'We check every load before pickup and flag at-risk transit early, so problems surface before they turn into penalties.',
  },
  {
    slug: 'claims-processing',
    title: 'Claims Processing',
    description:
      'We gather the documentation, validate the claim, and draft the response for your approval, so claims get filed and recovered.',
  },
  {
    slug: 'detention-handling',
    title: 'Detention Handling',
    description:
      'We warn the dock before free time runs out, and approve detention requests that match the gate record.',
  },
  {
    slug: 'purchase-order-follow-up',
    title: 'Purchase Order Follow-Up',
    description:
      'We find purchase orders due with no appointment, and get the supplier or carrier to book before stock runs short.',
  },
];

function WarehouseCustomerService() {
  return (
    <PageWrapper>
      <WarehouseCustomerServiceGlobalStyle />
      <Navigation theme="dark" />
      <Hero {...hero} />
      <WatchItRunYourFreight {...watchItRun} />
      <HowItWorks {...howItWorks} />
      <RunsOnYourStack />
      <TheRestOfTheLineup cards={lineupCards} />
      <EndCTA agentName="Warehouse Customer Service" />
      <Footer />
    </PageWrapper>
  );
}

export default WarehouseCustomerService;
export { WarehouseCustomerService };
